/**
 * Modelo de dominio: Suscripciones y planes.
 *
 * Los beneficios y limites de cada plan TODAVIA NO ESTAN DEFINIDOS. Por eso el
 * catalogo es una estructura de datos y no codigo: cambiar que ofrece cada plan
 * debe ser editar `PLAN_CATALOG`, nunca tocar componentes.
 *
 * La comprobacion de limites (`checkLimit`) tampoco conoce ningun limite
 * concreto: recibe la clave y la busca en el plan activo. Anadir un limite nuevo
 * es anadir una entrada al catalogo.
 */

export const PLAN_ID = {
  FREE: 'free',
  PRO: 'pro',
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending',           // pago iniciado, aun sin confirmar
  PAST_DUE: 'past_due',         // cobro fallido, acceso en gracia
  CANCELED: 'canceled',         // cancelada, activa hasta fin de periodo
  EXPIRED: 'expired',
  TRIALING: 'trialing',
};

export const STATUS_LABEL = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'Activa',
  [SUBSCRIPTION_STATUS.PENDING]: 'Pago en proceso',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'Pago pendiente',
  [SUBSCRIPTION_STATUS.CANCELED]: 'Cancelada',
  [SUBSCRIPTION_STATUS.EXPIRED]: 'Expirada',
  [SUBSCRIPTION_STATUS.TRIALING]: 'Periodo de prueba',
};

/** Claves de limite y de funcionalidad. */
export const LIMIT = {
  MAX_DEVICES: 'maxDevices',
  REFRESH_SECONDS: 'refreshSeconds',
  HISTORY_DAYS: 'historyDays',
};

export const FEATURE = {
  // La clave AUTO_CLOSE se conserva por compatibilidad con suscripciones ya
  // guardadas; ahora representa horarios autorizados por el usuario, nunca un
  // cierre de emergencia inferido por el software.
  BILL_ESTIMATE: 'billEstimate',
  AUTO_CLOSE: 'autoClose',
  SUSPICIOUS_USE_ALERT: 'suspiciousUseAlert',
  // Solo Pro: configurar la proteccion y gastar menos.
  AWAY_MODE: 'awayMode',
  CUSTOM_RULES: 'customRules',
  PROLONGED_USE_ALERT: 'prolongedUseAlert',
  BUDGET_ALERT: 'budgetAlert',
  PERIOD_COMPARISON: 'periodComparison',
  LIVE_VIEW: 'liveView',
  MONTHLY_REPORT: 'monthlyReport',
  DATA_EXPORT: 'dataExport',
  MULTI_USER: 'multiUser',
  PRIORITY_SUPPORT: 'prioritySupport',
};

/** `null` en un limite significa "sin limite". */
export const UNLIMITED = null;

/**
 * CATALOGO DE PLANES.
 *
 * Cuando el backend exista, esto debe venir de `GET /plans` y quedarse aqui
 * unicamente como valor por defecto para el primer render. El precio nunca debe
 * decidirse en el cliente: se muestra el que envie el servidor.
 *
 * OJO: hoy estos limites NO se aplican. El backend no conoce planes ni tiene
 * tabla de suscripciones, asi que cualquiera los salta llamando a la API
 * directamente. Esto es lo que la interfaz promete, no lo que el sistema
 * garantiza.
 */
export const PLAN_CATALOG = [
  {
    id: PLAN_ID.FREE,
    name: 'Gratuito',
    tagline: 'Vigila tu toma de agua y corta el paso si algo va mal',
    price: { amount: 0, currency: 'MXN', interval: 'month' },
    highlighted: false,
    limits: {
      [LIMIT.MAX_DEVICES]: 1,
      [LIMIT.REFRESH_SECONDS]: 60,
      [LIMIT.HISTORY_DAYS]: 30,
    },
    features: {
      [FEATURE.BILL_ESTIMATE]: true,
      [FEATURE.AUTO_CLOSE]: true,
      [FEATURE.SUSPICIOUS_USE_ALERT]: true,
      [FEATURE.AWAY_MODE]: false,
      [FEATURE.CUSTOM_RULES]: false,
      [FEATURE.PROLONGED_USE_ALERT]: false,
      [FEATURE.BUDGET_ALERT]: false,
      [FEATURE.PERIOD_COMPARISON]: false,
      [FEATURE.LIVE_VIEW]: false,
      [FEATURE.MONTHLY_REPORT]: false,
      [FEATURE.DATA_EXPORT]: false,
      [FEATURE.MULTI_USER]: false,
      [FEATURE.PRIORITY_SUPPORT]: false,
    },
  },
  {
    id: PLAN_ID.PRO,
    name: 'Pro',
    tagline: 'Protege varias tomas y controla lo que gastas',
    price: { amount: 199, currency: 'MXN', interval: 'month' },
    highlighted: true,
    limits: {
      [LIMIT.MAX_DEVICES]: 3,
      [LIMIT.REFRESH_SECONDS]: 5,
      [LIMIT.HISTORY_DAYS]: 180,
    },
    features: {
      [FEATURE.BILL_ESTIMATE]: true,
      [FEATURE.AUTO_CLOSE]: true,
      [FEATURE.SUSPICIOUS_USE_ALERT]: true,
      [FEATURE.AWAY_MODE]: true,
      [FEATURE.CUSTOM_RULES]: true,
      [FEATURE.PROLONGED_USE_ALERT]: true,
      [FEATURE.BUDGET_ALERT]: true,
      [FEATURE.PERIOD_COMPARISON]: true,
      [FEATURE.LIVE_VIEW]: true,
      [FEATURE.MONTHLY_REPORT]: true,
      [FEATURE.DATA_EXPORT]: true,
      [FEATURE.MULTI_USER]: true,
      [FEATURE.PRIORITY_SUPPORT]: true,
    },
  },
];

/** Etiquetas legibles. Separadas del catalogo para poder traducirlas aparte. */
export const LIMIT_LABEL = {
  [LIMIT.MAX_DEVICES]: 'Dispositivos vinculados',
  [LIMIT.REFRESH_SECONDS]: 'Frecuencia de lectura',
  [LIMIT.HISTORY_DAYS]: 'Historial disponible',
};

export const FEATURE_LABEL = {
  [FEATURE.BILL_ESTIMATE]: 'Consumo del mes en pesos',
  [FEATURE.AUTO_CLOSE]: 'Programación horaria de la válvula',
  [FEATURE.SUSPICIOUS_USE_ALERT]: 'Aviso de consumo sospechoso',
  [FEATURE.AWAY_MODE]: 'Modo ausente',
  [FEATURE.CUSTOM_RULES]: 'Reglas propias de cierre',
  [FEATURE.PROLONGED_USE_ALERT]: 'Aviso de consumo prolongado',
  [FEATURE.BUDGET_ALERT]: 'Aviso de presupuesto',
  [FEATURE.PERIOD_COMPARISON]: 'Comparativa entre periodos',
  [FEATURE.LIVE_VIEW]: 'Vista en vivo',
  [FEATURE.MONTHLY_REPORT]: 'Informe mensual',
  [FEATURE.DATA_EXPORT]: 'Exportación de datos',
  [FEATURE.MULTI_USER]: 'Varios usuarios por cuenta',
  [FEATURE.PRIORITY_SUPPORT]: 'Soporte prioritario',
};

/** Formatea el valor de un limite para mostrarlo. */
export function formatLimit(key, value) {
  if (value === UNLIMITED) return 'Sin límite';
  if (value === 0) return 'No incluido';
  switch (key) {
    case LIMIT.HISTORY_DAYS: {
      if (value >= 365) return `${Math.round(value / 365)} año(s)`;
      // 180 dias se lee mejor como "6 meses": es como lo piensa quien compara
      // recibos, que es para lo que sirve el historial.
      if (value >= 60) return `${Math.round(value / 30)} meses`;
      return `${value} días`;
    }
    case LIMIT.MAX_DEVICES:
      return value === 1 ? '1 dispositivo' : `${value} dispositivos`;
    case LIMIT.REFRESH_SECONDS: {
      if (value === 60) return 'Cada minuto';
      return value > 60 ? `Cada ${Math.round(value / 60)} min` : `Cada ${value} s`;
    }
    default:
      return String(value);
  }
}

export function getPlan(planId) {
  return PLAN_CATALOG.find((p) => p.id === planId) ?? PLAN_CATALOG[0];
}

/**
 * Comprueba si un uso concreto cabe en el plan.
 *
 * Devuelve siempre la misma forma para que la interfaz pueda reaccionar sin
 * conocer el limite: {allowed, limit, current, remaining, reason}.
 *
 * PUNTO DE MIGRACION: el cliente hace esta comprobacion para ANTICIPARSE y
 * mostrar avisos, nunca para autorizar. La autorizacion real es del backend,
 * porque cualquiera puede manipular el estado del navegador.
 */
export function checkLimit(subscription, limitKey, currentUsage) {
  const plan = getPlan(subscription?.planId);
  const limit = plan.limits[limitKey];

  if (limit === UNLIMITED) {
    return { allowed: true, limit: UNLIMITED, current: currentUsage, remaining: Infinity };
  }
  const remaining = limit - currentUsage;
  return {
    allowed: remaining > 0,
    limit,
    current: currentUsage,
    remaining: Math.max(0, remaining),
    reason: remaining > 0 ? null : `Tu plan ${plan.name} permite ${formatLimit(limitKey, limit)}.`,
  };
}

/** Si el plan activo incluye una funcionalidad. Misma advertencia que checkLimit. */
export function hasFeature(subscription, featureKey) {
  return Boolean(getPlan(subscription?.planId).features[featureKey]);
}

/**
 * Si la suscripcion da acceso ahora mismo.
 * `canceled` sigue dando acceso hasta que vence el periodo ya pagado: cancelar
 * no es lo mismo que expirar, y confundirlos es un error clasico de facturacion.
 */
export function grantsAccess(subscription) {
  if (!subscription) return false;
  const { status, currentPeriodEnd } = subscription;
  if (status === SUBSCRIPTION_STATUS.ACTIVE) return true;
  if (status === SUBSCRIPTION_STATUS.TRIALING) return true;
  if (status === SUBSCRIPTION_STATUS.PAST_DUE) return true;   // periodo de gracia
  if (status === SUBSCRIPTION_STATUS.CANCELED) {
    return currentPeriodEnd ? Date.now() < new Date(currentPeriodEnd).getTime() : false;
  }
  return false;
}

/**
 * Normaliza la suscripcion que llegue de la fuente de datos.
 * PUNTO DE MIGRACION: unico lugar que debe conocer el contrato del backend o
 * del proveedor de pagos.
 */
export function normalizeSubscription(raw) {
  if (!raw) return null;
  return {
    planId: raw.plan_id ?? raw.planId ?? PLAN_ID.FREE,
    status: raw.status ?? SUBSCRIPTION_STATUS.ACTIVE,
    currentPeriodStart: raw.current_period_start ?? raw.currentPeriodStart ?? null,
    currentPeriodEnd: raw.current_period_end ?? raw.currentPeriodEnd ?? null,
    cancelAtPeriodEnd: Boolean(raw.cancel_at_period_end ?? raw.cancelAtPeriodEnd),
    // El backend NUNCA debe exponer datos de tarjeta: solo estos cuatro campos
    // que el proveedor de pagos considera seguros para mostrar.
    paymentMethod: raw.payment_method
      ? {
          brand: raw.payment_method.brand,
          last4: raw.payment_method.last4,
          expMonth: raw.payment_method.exp_month,
          expYear: raw.payment_method.exp_year,
        }
      : null,
  };
}
